import { forwardRef, useRef, type ReactNode, type PointerEvent, type MouseEvent } from "react";

// Horizontal scroll row with click-and-drag scrolling. Pointer events unify
// mouse + touch, so dragging works on desktop (where there's no swipe) AND
// on mobile even when native overflow scrolling is flaky. `touch-action:
// pan-y` keeps vertical page scrolling native while horizontal moves are
// ours. Links inside stay clickable — a real drag (>6px) suppresses the
// click that would otherwise fire on pointer-up.
interface Props {
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
}

export const DragScrollRow = forwardRef<HTMLDivElement, Props>(function DragScrollRow(
  { children, className = "", ariaLabel },
  outerRef,
) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef({ active: false, moved: false, startX: 0, startLeft: 0 });

  const setRefs = (el: HTMLDivElement | null) => {
    innerRef.current = el;
    if (typeof outerRef === "function") outerRef(el);
    else if (outerRef) outerRef.current = el;
  };

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    const el = innerRef.current;
    if (!el) return;
    drag.current = { active: true, moved: false, startX: e.clientX, startLeft: el.scrollLeft };
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    const el = innerRef.current;
    if (!el || !drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    if (!drag.current.moved && Math.abs(dx) < 6) return; // click tolerance
    if (!drag.current.moved) {
      drag.current.moved = true;
      el.setPointerCapture(e.pointerId);
    }
    el.scrollLeft = drag.current.startLeft - dx;
  }

  function endDrag(e: PointerEvent<HTMLDivElement>) {
    const el = innerRef.current;
    if (el && drag.current.moved && el.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
    drag.current.active = false;
    // `moved` stays true until the click event fires (see onClickCapture).
  }

  function onClickCapture(e: MouseEvent<HTMLDivElement>) {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  }

  return (
    <div
      ref={setRefs}
      role={ariaLabel ? "region" : undefined}
      aria-label={ariaLabel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClickCapture={onClickCapture}
      className={[
        "flex overflow-x-auto w-full min-w-0 select-none cursor-grab active:cursor-grabbing",
        "[touch-action:pan-y] [overscroll-behavior-x:contain]",
        "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
});
